import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { keys, pathOr, omit } from 'ramda';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import RecertificationApplicationForm from './RecertificationApplicationForm';
import RecertificationApplicationV2 from './RecertificationApplicationV2';
import { studentsTypes } from '../PrimaryLeaseApplication/AffordablePrimaryForm/configuration';
import * as recertificationActions from './actions';
import { getAllStates, getAllEmploymentStatuses } from '../App/actions';
import {
  getStateOptions,
  getEmploymentStatusesOptions,
} from '../App/selectors';
import { AFFORDABLE_PROGRAMS } from '../../constants/affordableProgramNames';
import {
  INITIAL_CERT_TYPE,
  INITIAL_UT_CERT_TYPE,
} from '../AffordableQualificationTab/HUDTab/constants';

import AffordableQualificationService from '../../services/affordableQualificationService';
import {
  Spinner,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { FormattedMessage } from 'react-intl';
import messages from './messages.js';
import { getSettingsByPropertyAndProgram } from '../GeneralAffordable/utils';
import {
  getCertTabFromLocation,
  getQualificationFormDisabledState,
  isReopenModifyHudCertsEnabled,
  buildCertNavSearch,
} from '../QualificationForms/utils';
import type { GlobalState, SelectOption } from '../App/types';
import { TAB_KEYS } from '../ResidentProfile/constants';
import { getURLProgramName } from '../../utils/affordable';
import store from '../../store';

type Props = {
  actions: Object,
  intl: Object,
  history: Object,
  change: Function,
  selectedProperty: Object,
  employmentStatuses: Array<SelectOption>,
  states: Array<SelectOption>,
  submit: Function,
  recertificationFormData: Object,
  residentId: string,
  affordableQualificationId: string,
  applicantId: string,
  applicationId: string,
  householdId: string,
  programName: string,
};
type State = {};

export class RecertificationApplicationV1 extends Component<State & Props> {
  constructor() {
    super();
    this.state = {
      affordableQualification: {},
      isLoading: true,
      isError: false,
    };
  }
  async componentDidMount() {
    const {
      affordableQualificationId,
      applicantId,
      actions: {
        getAllStates,
        getAllEmploymentStatuses,
        getRecertificationFormInformation,
      },
      selectedProperty: { id: propertyId, organizationId },
    } = this.props;

    getAllStates();
    getAllEmploymentStatuses();
    getRecertificationFormInformation(applicantId, affordableQualificationId);
    await this.fetchAffordableQualification(
      organizationId,
      propertyId,
      affordableQualificationId,
    );
  }

  componentWillUnmount() {
    this.props.actions.cleanRecertificationFormInformation();
  }

  getIsInitialCertForNonHUD = (): boolean => {
    const { selectedProperty, programName, flags, recertificationFormData } =
      this.props;
    const { affordableQualificationCertificationType } =
      recertificationFormData;
    const formattedSetup = getSettingsByPropertyAndProgram(
      selectedProperty?.setup || {},
      programName,
    );
    const requiresInitialCertificationForNonHUD =
      formattedSetup.requiresInitialCertification ?? false;
    const requiresLIHTCInitialCertFlag =
      (flags?.initialCertsForLihtc ?? false) &&
      requiresInitialCertificationForNonHUD;
    const isNonHUDProgram = programName !== AFFORDABLE_PROGRAMS.HUD;
    return (
      [INITIAL_CERT_TYPE, INITIAL_UT_CERT_TYPE].includes(
        affordableQualificationCertificationType,
      ) &&
      isNonHUDProgram &&
      requiresLIHTCInitialCertFlag
    );
  };

  fetchAffordableQualification = async (
    organizationId,
    propertyId,
    affordableQualificationId,
  ) => {
    try {
      const aqService = new AffordableQualificationService();
      const affordableQual = await aqService.getById(
        organizationId,
        propertyId,
        affordableQualificationId,
      );

      this.setState({
        affordableQualification: affordableQual,
        isLoading: false,
        isError: false,
      });
    } catch (error) {
      this.setState({
        affordableQualification: {},
        isLoading: false,
        isError: true,
      });
    }
  };

  handleCancel = () => {
    const { residentId, programName } = this.props;
    const urlProgramName = programName
      ? getURLProgramName(programName)
      : TAB_KEYS.HUD;
    const path = `/resident/${residentId}/?tab=${urlProgramName}${buildCertNavSearch(
      this.props.location,
    )}`;
    const selectedPropertyId = store.getState()?.app?.selectedProperty?.id;
    const urlToNavigateTo = selectedPropertyId
      ? `/property/${selectedPropertyId}${path}`
      : path;
    this.props.history.push(urlToNavigateTo);
  };

  handleSubmit = (formData: Object) => {
    const massageEmployerInfo = (employerInfo: Object): Object => {
      const { phoneNumber, address } = employerInfo;
      let employerAddress;
      if (address) {
        employerAddress = {
          employerStreetAddress: address.street,
          ...omit(['street'], address),
        };
      }
      const employerObject = omit(['phoneNumber', 'address'], employerInfo);
      const setNulls = (obj: Object) => (withNulls, key) =>
        obj[key] === ''
          ? { ...withNulls, [key]: null }
          : { ...withNulls, [key]: obj[key] };
      const finalEmployerObject = keys(employerObject).reduce(
        setNulls(employerObject),
        {},
      );
      const finalEmployerAddress = employerAddress
        ? keys(employerAddress).reduce(setNulls(employerAddress), {})
        : null;
      return {
        ...finalEmployerObject,
        ...finalEmployerAddress,
        employerPhone: phoneNumber,
      };
    };

    const {
      actions,
      applicantId,
      residentId,
      applicationId,
      programName,
      affordableQualificationId,
    } = this.props;

    const {
      applicantsCurrentEmployer,
      applicantsSecondCurrentEmployer,
      applicantsPreviousEmployer,
      studentInformation,
      employmentInformation,
      hasSignedCopy,
      isComplete,
    } = formData;

    const isInitialCertForNonHUD = this.getIsInitialCertForNonHUD();

    const yesNoDict = { yes: true, no: false };

    const changeHousehold = pathOr(
      null,
      ['changeHouseholdInformation'],
      formData,
    );
    const anticipateChangeHouseholdString = pathOr(
      null,
      ['changeHouseholdInformation', 'anticipateChangeHousehold'],
      formData,
    );
    const anticipateChangeHousehold = anticipateChangeHouseholdString
      ? yesNoDict[anticipateChangeHouseholdString]
      : null;
    const changeHouseholdInformation = changeHousehold
      ? {
          ...changeHousehold,
          anticipateChangeHousehold,
        }
      : null;

    const currentEmployer = massageEmployerInfo(applicantsCurrentEmployer);
    const secondCurrentEmployer = massageEmployerInfo(
      applicantsSecondCurrentEmployer,
    );
    const previousEmployer = massageEmployerInfo(applicantsPreviousEmployer);
    const employmentStatusId = employmentInformation
      ? employmentInformation.status
      : null;

    const anticipateChangeEmployerString = pathOr(
      null,
      ['anticipateChangeEmployer'],
      employmentInformation,
    );
    const anticipateChangeEmployer = anticipateChangeEmployerString
      ? yesNoDict[anticipateChangeEmployerString]
      : null;

    const recertFormData = {
      applicantsCurrentEmployer: currentEmployer,
      applicantsSecondCurrentEmployer: secondCurrentEmployer,
      applicantsPreviousEmployer: previousEmployer,
      employmentStatusId,
      studentInformation,
      changeHouseholdInformation,
      hasSignedCopy,
      anticipateChangeEmployer,
      isComplete,
      isInitialCertForNonHUD,
    };
    actions.saveRecertificationForm(
      recertFormData,
      applicantId,
      residentId,
      applicationId,
      programName,
      affordableQualificationId,
    );
  };

  handlePrintApplicationForm = () => {
    const {
      applicantId,
      affordableQualificationId,
      programName,
      selectedProperty: { id: propertyId, organizationId, name: propertyName },
    } = this.props;
    this.props.actions.printRecertificationApplicationForm(
      organizationId,
      propertyId,
      applicantId,
      affordableQualificationId,
      programName,
      propertyName,
    );
  };

  render() {
    const {
      intl,
      change,
      applicantId,
      householdId,
      selectedProperty,
      employmentStatuses,
      states,
      recertificationFormData,
      affordableQualificationId,
      programName,
      selectedProperty: { id: propertyId, organizationId },
      flags,
    } = this.props;
    const { isLoading, isError, affordableQualification } = this.state;

    const isComplete = pathOr('', ['isComplete'], recertificationFormData);
    const propertyName = pathOr('', ['name'], selectedProperty);
    const isInitialCertForNonHUD = this.getIsInitialCertForNonHUD();
    const certTab = getCertTabFromLocation(this.props.location);
    const { isDisabled: isFormDisabled } = getQualificationFormDisabledState({
      affordableQualification,
      flags,
      programName,
      certTab,
    });

    return isError ? (
      <Grid container align="center" justify="center">
        <Grid item xs={12}>
          <Typography sx={{ fontSize: '12px' }}>
            <FormattedMessage {...messages.loadError} />
            <button
              onClick={() => {
                this.fetchAffordableQualification(
                  organizationId,
                  propertyId,
                  affordableQualificationId,
                );
              }}
            >
              <FormattedMessage {...messages.retry} />
            </button>
          </Typography>
        </Grid>
      </Grid>
    ) : isLoading ? (
      <Spinner />
    ) : (
      <RecertificationApplicationForm
        intl={intl}
        change={change}
        applicantId={applicantId}
        householdId={householdId}
        employmentStatuses={employmentStatuses}
        states={states}
        propertyName={propertyName}
        handleCancel={this.handleCancel}
        onSubmit={this.handleSubmit}
        studentsTypes={studentsTypes}
        initialValues={recertificationFormData}
        isComplete={isComplete}
        handlePrintRecertApplicationForm={this.handlePrintApplicationForm}
        isInitialCertForNonHUD={isInitialCertForNonHUD}
        isFormDisabled={isFormDisabled}
      />
    );
  }
}

const RecertificationApplicationSelector = (props) => {
  const { flags, programName } = props;
  const useV2 =
    isReopenModifyHudCertsEnabled(flags) &&
    programName === AFFORDABLE_PROGRAMS.HUD;

  return useV2 ? (
    <RecertificationApplicationV2 {...props} />
  ) : (
    <RecertificationApplicationV1 {...props} />
  );
};

export const mapStateToProps = (
  state: GlobalState,
  ownProps: Object,
): Object => {
  const { app, recertificationFormData } = state;
  return {
    affordableQualificationId: ownProps.match.params.affordableQualificationId,
    applicantId: ownProps.match.params.applicantId,
    residentId: ownProps.match.params.residentId,
    applicationId: ownProps.match.params.applicationId,
    householdId: ownProps.match.params.householdId,
    programName: ownProps.match.params.programName,
    employmentStatuses: getEmploymentStatusesOptions(state),
    states: getStateOptions(state),
    selectedProperty: app.selectedProperty,
    recertificationFormData,
  };
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators(
    {
      ...recertificationActions,
      getAllStates,
      getAllEmploymentStatuses,
    },
    dispatch,
  );
  return { actions };
}

const connected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(RecertificationApplicationSelector));

export default withLDConsumer()(connected);
